import { useRef, useState } from 'react';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import Container from '../ui/Container';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import Icon from '../ui/Icon';
import Button from '../ui/Button';
import useReducedMotion from '../../hooks/useReducedMotion';
import { processSteps } from '../../data/process';
import cn from '../../lib/cn';

/**
 * Interactive workflow timeline.
 * Mobile: single column with the spine on the left.
 * Desktop: alternating cards either side of a centred spine that fills on scroll.
 */
export default function ProcessTimeline({ compact = false, className }) {
  const trackRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const [openStep, setOpenStep] = useState(1);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start 70%', 'end 60%'],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 26, restDelta: 0.001 });

  const steps = compact ? processSteps.slice(0, 4) : processSteps;

  return (
    <section className={cn('section bg-white', className)} id="process">
      <Container>
        <SectionHeading
          eyebrow="How it works"
          title="Eight steps from pickup request to closed obligation"
          description="A predictable sequence with a named owner at every stage. You always know where your consignment is and which document comes next."
        />

        <div ref={trackRef} className="relative mt-16">
          {/* Spine */}
          <div
            className="absolute bottom-0 left-7 top-0 w-0.5 rounded-full bg-ink-100 lg:left-1/2 lg:-translate-x-1/2"
            aria-hidden="true"
          >
            <motion.div
              className="h-full w-full origin-top rounded-full bg-gradient-to-b from-brand-400 via-brand-600 to-brand-800"
              style={reducedMotion ? { scaleY: 1 } : { scaleY: progress }}
            />
          </div>

          <ol className="space-y-8 lg:space-y-4">
            {steps.map((step, index) => {
              const isOpen = openStep === step.id;
              const onLeft = index % 2 === 0;

              return (
                <li key={step.id} className="relative pl-20 lg:pl-0">
                  {/* Node — pinned to the spine at both breakpoints */}
                  <div className="absolute left-0 top-1 z-10 lg:left-1/2 lg:-translate-x-1/2">
                    <button
                      type="button"
                      onClick={() => setOpenStep(isOpen ? null : step.id)}
                      aria-label={`Step ${step.id}: ${step.title}`}
                      className={cn(
                        'relative grid h-14 w-14 place-items-center rounded-2xl border-2 transition-all duration-300',
                        isOpen
                          ? 'border-brand-600 bg-brand-600 text-white shadow-lift'
                          : 'border-ink-200 bg-white text-ink-500 hover:border-brand-400 hover:text-brand-600'
                      )}
                    >
                      <Icon name={step.icon} className="h-6 w-6" />
                      <span
                        className={cn(
                          'absolute -right-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full text-[0.68rem] font-bold transition-colors',
                          isOpen ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-600'
                        )}
                      >
                        {step.id}
                      </span>
                    </button>
                  </div>

                  {/* Card — half width and side-alternating from lg up */}
                  <Reveal
                    direction={onLeft ? 'right' : 'left'}
                    className={cn(
                      'lg:w-[calc(50%-3.25rem)]',
                      onLeft ? 'lg:mr-auto' : 'lg:ml-auto'
                    )}
                  >
                    <StepCard
                      step={step}
                      isOpen={isOpen}
                      onToggle={setOpenStep}
                      alignRight={onLeft}
                    />
                  </Reveal>
                </li>
              );
            })}
          </ol>
        </div>

        {compact && (
          <Reveal className="mt-12 flex justify-center">
            <Button to="/process" variant="outline" size="lg">
              See the full 8-step workflow
            </Button>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

/** Single expandable timeline card. `alignRight` mirrors it toward the spine. */
function StepCard({ step, isOpen, onToggle, alignRight }) {
  return (
    <div
      className={cn(
        'rounded-xl2 border bg-white p-6 transition-all duration-300 ease-smooth',
        isOpen
          ? 'border-brand-200 shadow-lift'
          : 'border-ink-100 shadow-soft hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lift',
        alignRight && 'lg:text-right'
      )}
    >
      <span className="inline-block rounded-full bg-brand-50 px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-wider text-brand-700">
        {step.duration}
      </span>

      <button
        type="button"
        onClick={() => onToggle(isOpen ? null : step.id)}
        aria-expanded={isOpen}
        className={cn(
          'mt-3 flex w-full items-center gap-3 text-left',
          alignRight && 'lg:flex-row-reverse lg:text-right'
        )}
      >
        <h3 className="flex-1 font-display text-lg font-semibold text-ink-900">{step.title}</h3>
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-none text-ink-400 transition-transform duration-300',
            isOpen && 'rotate-180 text-brand-600'
          )}
        />
      </button>

      <p className="mt-2.5 text-sm leading-relaxed text-ink-500">{step.summary}</p>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <ul className="mt-5 space-y-2 border-t border-ink-100 pt-4">
              {step.details.map((detail) => (
                <li
                  key={detail}
                  className={cn(
                    'flex gap-2.5 text-sm text-ink-600',
                    alignRight && 'lg:flex-row-reverse'
                  )}
                >
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-brand-500" />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
